'use client'

import { ChevronDown, ChevronUp, InfoIcon, SparklesIcon } from 'lucide-react'
import Button from '../Button/Button'
import { useState, useTransition } from 'react'
import { summarizeAction } from './summarizeAction'
import { Row, Stack } from '@christiankaindl/lyts'
import Text from '../Text/Text'
import Spinner from '../Spinner/Spinner'
import { card } from '@/styles/theme.css'
import Pill from '../Pill/Pill'
import RefButton from '../Norm/RefButton'
import Heading from '../Heading/Heading'
import type { IDecision } from '@/lib/ris-types'
import type { ParserLink } from '@gesetzefinden-at/core'
import {
  Disclosure,
  DisclosureContent,
  useDisclosureStore,
} from '@ariakit/react'
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip'

export default function SummarizeButton ({ id, ...props }: { id: string, refs: ParserLink[] } & IDecision['summary']) {
  const [isPending, startTransition] = useTransition()
  const [summary, setSummary] = useState<({ refs: any[] } & IDecision['summary']) | null>(props ?? null)
  const [isError, setIsError] = useState(false)

  const disclosure = useDisclosureStore()
  const isOpen = disclosure.useState('open')

  async function handleClick () {
    startTransition(async () => {
      try {
        setIsError(false)
        const _summary = await summarizeAction(id)
        disclosure.setOpen(true)
        setSummary(_summary)
      } catch (error) {
        console.error(error)
        setIsError(true)
      }
    })
  }

  return (
    <Stack style={{ marginLeft: -12, marginTop: -24, marginBottom: -24 }}>
      {!summary?.title && (
        // @ts-expect-error icon
        <Button disabled={summary?.title ?? isPending} style={{ color: '#A48757', opacity: 1 }} onClick={() => { handleClick().catch(console.error) }} variant='flat' size='small' icon={isPending ? Spinner : SparklesIcon}>
          AmigaAI <span style={{ fontWeight: 300 }}>Summary</span>
        </Button>
      )}
      {isError && (
        <Text>Ein Fehler aufgetreten.</Text>
      )}
      {summary?.title && (
        <Stack gap={1.25} className={card} style={{ paddingTop: 0, paddingBottom: 0 }}>
          <Disclosure render={<Row xAlign='space-between' yAlign='start' />} store={disclosure} style={{ padding: '1em 0', cursor: 'pointer' }}>
            <Stack gap={0.75}>
              <Row gap={0} style={{ margin: '-3px 0px -9px -9px' }}>
                {/* @ts-expect-error icon */}
                <Button disabled style={{ color: '#A48757', opacity: 1 }} variant='flat' size='small' icon={isPending ? Spinner : SparklesIcon}>
                  AmigaAI <span style={{ fontWeight: 300 }}>Summary</span>
                </Button>
                <Tooltip>
                  <TooltipTrigger render={<InfoIcon size={16} />} style={{ opacity: 0.4 }} />
                  <TooltipContent>
                    Das Summary ist KI-generiert
                  </TooltipContent>
                </Tooltip>
              </Row>
              <Heading h={5}>{summary.title}</Heading>
              <Row wrap gap={0.5}>
                {summary.keywords?.map((keyword) => {
                  return (
                    <Pill key={keyword}>{keyword}</Pill>
                  )
                })}
              </Row>
            </Stack>
            <Button circular square>
              {isOpen && (<ChevronUp size={20} />)}
              {!isOpen && (<ChevronDown size={20} />)}
            </Button>
          </Disclosure>
          <DisclosureContent render={<Stack />} store={disclosure}>
            <Text><b>Sachverhalt: </b>{summary.facts}</Text>
            <Text><b>Spruch: </b>{summary.verdict}</Text>
            <Text><b>Rechtliche Beurteilung: </b>{summary.legalAssessment}</Text>
            {summary?.refs && (
              <Row gap={0.5} wrap style={{ marginBottom: 15 }}>
                {summary.refs?.map((ref) => {
                  return (
                    <RefButton key={ref.id} {...ref} id={ref.citation} />
                  )
                })}
              </Row>
            )}
          </DisclosureContent>
        </Stack>
      )}
    </Stack>
  )
}
